import {
  Typography,
  Box,
  Paper,
  IconButton,
  Divider,
  Tabs,
  Tab,
} from '@mui/material'
import {
  QrCode2 as QrCodeIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon,
} from '@mui/icons-material'

import DeleteCustomImplantModal from '../DeleteCustomImplantModal'
import UsageCustomImplantModal from 'components/organisms/UsageCustomImplantModal/UsageCustomImplantModal'
import './CustomImplantTray.scss'
import useCustomImplantTrayLogic from './CustomImplantTray.logic'

const CustomImplantTray = ({ isInOR = false }: { isInOR?: boolean }) => {
  const {
    customImplants,
    deleteModalOpen,
    selectedImplant,
    groupedImplants,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleRemoveImplant,
    usageModalOpen,
    handleOpenUsageModal,
    handleCloseUsageModal,
    usageCustomImplants,
    handleReturnImplant,
    activeTab,
    handleTabChange,
  } = useCustomImplantTrayLogic({ isInOR })

  const renderImplantCard = (implant: any, isUsed: boolean = false) => (
    <Paper key={implant.deviceId} className="implant-card">
      <Box className="implant-header">
        <Box className="qr-icon-container">
          <QrCodeIcon />
        </Box>
        <Typography variant="h6" className="implant-title">
          {implant.deviceDescription}
        </Typography>
        {implant.count > 1 && !isUsed && (
          <Box className="count-badge">
            <Typography variant="body2">{implant.count}</Typography>
          </Box>
        )}
        {isInOR ? (
          isUsed ? (
            <IconButton
              size="small"
              className="return-button"
              onClick={() => handleReturnImplant(implant)}
            >
              <UndoIcon />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              className="usage-button"
              onClick={() => handleOpenUsageModal(implant)}
            >
              <CheckCircleIcon />
            </IconButton>
          )
        ) : (
          <IconButton
            size="small"
            className="delete-button"
            onClick={() => handleOpenDeleteModal(implant)}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      <Divider className="divider" />

      <Box className="implant-details">
        <Box className="detail-row">
          <Typography variant="body2" className="detail-label">
            Device ID:
          </Typography>
          <Typography variant="body2" className="detail-value">
            {implant.deviceId}
          </Typography>
        </Box>
        {isUsed && (
          <Box className="status-indicator">
            <Typography variant="body2" className="status-text">
              {implant.wasted ? 'Wasted' : 'Implanted'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  )

  return (
    <div className="custom-implant-tray">
      {isInOR ? (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className="implant-tabs"
          >
            <Tab label="Available" />
            <Tab label="Used" />
          </Tabs>

          {activeTab === 0 ? (
            groupedImplants.length === 0 ? (
              <Box className="empty-state">
                <Typography variant="body1" color="textSecondary">
                  No custom implants available.
                </Typography>
              </Box>
            ) : (
              <Box className="implants-container">
                {groupedImplants.map((implant) => renderImplantCard(implant))}
              </Box>
            )
          ) : usageCustomImplants.length === 0 ? (
            <Box className="empty-state">
              <Typography variant="body1" color="textSecondary">
                No custom implants have been used yet.
              </Typography>
            </Box>
          ) : (
            <Box className="implants-container">
              {usageCustomImplants.map((implant) =>
                renderImplantCard(implant, true)
              )}
            </Box>
          )}
        </>
      ) : (
        <>
          {customImplants.length === 0 ? (
            <Box className="empty-state">
              <Typography variant="body1" color="textSecondary">
                No custom implants added yet.
              </Typography>
            </Box>
          ) : (
            <Box className="implants-container">
              {groupedImplants.map((implant) => renderImplantCard(implant))}
            </Box>
          )}
        </>
      )}

      {!isInOR && (
        <DeleteCustomImplantModal
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          implant={selectedImplant}
          onConfirm={handleRemoveImplant}
        />
      )}

      {isInOR && (
        <UsageCustomImplantModal
          open={usageModalOpen}
          onClose={handleCloseUsageModal}
          implant={selectedImplant}
        />
      )}
    </div>
  )
}

export default CustomImplantTray
